<template>
  <div class="chat_wrap">
    <!-- 左：会话列表 -->
    <div class="chat_side">
      <div class="side_head">
        <span class="side_label">会话</span>
        <el-button type="text" icon="el-icon-plus" :disabled="!agentId" @click="newSession">新对话</el-button>
      </div>
      <div class="side_list" v-loading="sessionLoading">
        <div v-for="s in sessionList" :key="s.session_id"
             :class="['side_item', { active: s.session_id === currentSessionId }]"
             @click="switchSession(s)">
          <div class="side_title">{{ s.title || '新会话' }}</div>
          <div class="side_time">{{ fmtTime(s.utime || s.ctime) }}</div>
        </div>
        <div v-if="!sessionLoading && !sessionList.length" class="side_empty">暂无会话</div>
      </div>
    </div>

    <!-- 右侧：对话区（数字员工改在页面右侧功能列下方以列表选择） -->
    <div class="chat_right">
      <div class="chat_main">
        <div class="chat_head">
          <!-- 员工选择在右侧功能列下方的列表完成，这里只显示当前对话对象 -->
          <div class="chat_agent">
            <i class="el-icon-user-solid chat_agent_icon"></i>
            <span class="chat_agent_name">{{ currentAgentName }}</span>
            <!-- 当前员工是某个生效团队的队长时，展示团队协作标识（编排由服务端自动触发） -->
            <el-tooltip v-if="currentTeam" :content="teamTip" placement="bottom">
              <el-tag size="mini" type="warning" class="team_badge">
                团队协作 · {{ (currentTeam.members || []).length + 1 }}人
              </el-tag>
            </el-tooltip>
          </div>
          <el-tooltip content="关闭后使用一次性返回（流式异常时的兜底）" placement="bottom">
            <span class="stream_switch">流式输出<el-switch v-model="streamEnabled" style="margin-left: 6px" /></span>
          </el-tooltip>
        </div>

        <div class="msg_box" ref="msgBox" @scroll="onScroll">
          <div v-for="(m, i) in messages" :key="i" :class="['msg_row', 'msg_' + m.role]">
            <div v-if="m.role === 'user'" class="bubble user_bubble">{{ m.content }}</div>
            <!-- 历史 role=tool 消息：服务端独立落库的工具调用记录，折叠卡片展示 -->
            <div v-else-if="m.role === 'tool'" class="tool_hist">
              <div class="tool_hist_head" @click="m.open = !m.open">
                <i class="el-icon-s-operation" />
                <span>本回合工具调用{{ m.items ? '（' + m.items.length + ' 次）' : '' }}</span>
                <i :class="m.open ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
              </div>
              <tool-cards v-if="m.open && m.items && m.items.length" :items="m.items" class="tool_hist_body" />
              <pre v-else-if="m.open" class="tool_hist_raw">{{ m.content }}</pre>
            </div>
            <div v-else class="asst_block">
              <!-- 首包未到（无任何内容）时的思考占位，任一内容到达即消失 -->
              <div v-if="m.state === 'streaming' && !m.content && !m.thinks && !m.tools.length" class="thinking_bar">
                <i class="el-icon-loading" /> 正在思考…
              </div>
              <el-collapse v-if="m.thinks" v-model="m.thinkOpen" class="think_collapse">
                <el-collapse-item name="t" title="思考过程">
                  <pre class="think_text">{{ m.thinks }}</pre>
                </el-collapse-item>
              </el-collapse>
              <tool-cards v-if="m.tools && m.tools.length" :items="m.tools" />
              <!-- html 与 content 成对写入（流式/done/fail/历史路径），只渲染消毒后的 html 防后续路径漏消毒 -->
              <div v-if="m.content" class="bubble asst_bubble md_body" v-html="m.html"></div>
              <!-- 失败态：断线重试复用原 id 取缓存回复；服务端已判失败的轮次自动换新 id 重新执行 -->
              <div v-if="m.state === 'failed'" class="fail_bar">
                发送失败
                <el-button type="text" size="mini" @click="retryTurn(m)">点击重试</el-button>
              </div>
            </div>
          </div>
          <div v-if="!messages.length" class="msg_empty">{{ agentId ? '开始对话吧' : '请先选择数字员工' }}</div>
        </div>

        <div class="input_tools">
          <!-- select 场景匹配：按输入内容推荐已发布通用技能，插入引用文案（不自动发送） -->
          <el-button type="text" icon="el-icon-magic-stick" :disabled="!agentId" @click="openSkillPick">推荐技能</el-button>
        </div>
        <div class="input_bar">
          <el-input type="textarea" v-model="input" :rows="3" resize="none" maxlength="4000"
                    placeholder="输入内容，Enter 发送，Shift+Enter 换行"
                    :disabled="!agentId" @keydown.native="onKeydown" />
          <el-button :type="sending ? 'danger' : 'primary'" class="send_btn"
                     :disabled="!sending && (!agentId || !input.trim())"
                     @click="sending ? stopStream() : send()">{{ sending ? '停 止' : '发 送' }}</el-button>
        </div>
      </div>
    </div>

    <!-- 推荐通用技能（select 词法匹配已发布技能） -->
    <el-dialog title="推荐通用技能" :visible.sync="pickVisible" width="560px" v-dialogDrag
               :modal-append-to-body="false" v-if="pickVisible">
      <div class="pick_bar">
        <el-input v-model="pickQuery" placeholder="输入任务场景描述，如：线索打分" maxlength="200"
                  @keyup.enter.native="searchSkills" />
        <el-button type="primary" :loading="pickLoading" @click="searchSkills">匹 配</el-button>
      </div>
      <div v-if="pickList.length" class="pick_list">
        <div v-for="s in pickList" :key="s.package_id" class="pick_item">
          <div class="pick_head">
            <b>{{ s.name }}</b>
            <el-tag size="mini" :type="s.runtime === 'python' ? '' : 'warning'">{{ s.runtime }}</el-tag>
          </div>
          <div class="pick_summary">{{ s.summary || '（无说明）' }}</div>
          <div class="pick_meta">
            <span v-if="s.inputs && s.inputs.length">入参：{{ s.inputs.join('、') }}</span>
            <span v-if="s.tags && s.tags.length">{{ s.tags.join('、') }}</span>
            <el-button type="text" size="mini" @click="insertSkill(s)">插入引用</el-button>
          </div>
        </div>
      </div>
      <div v-else-if="pickSearched && !pickLoading" class="pick_empty">暂无匹配的已发布通用技能</div>
    </el-dialog>
  </div>
</template>

<script>
// 对话面板：选 agent → 建/选会话 → 流式对话；reply 以 markdown 渲染，工具过程结构化卡片
// 历史 role=tool 消息为服务端独立落库的工具调用记录（JSON 数组），解析后折叠展示
import { agentBody, genTurnId, isAdminAcc, isEnterprise } from '../utils'
import { ssePost } from '../sse'
import { renderMarkdown } from '../render'
import ToolCards from './tool-cards.vue'

export default {
  name: 'AiAgentChat',
  components: { ToolCards },
  data() {
    return {
      agentOptions: [],
      agentId: '',
      // 生效团队列表：队长员工发起回合时服务端自动进入团队编排
      teams: [],
      streamEnabled: true,
      sessionList: [],
      sessionLoading: false,
      currentSessionId: '',
      messages: [],
      input: '',
      sending: false,
      autoScroll: true,
      abortCtrl: null,
      // 推荐技能弹窗（select 匹配）
      pickVisible: false,
      pickQuery: '',
      pickOrigInput: '',
      pickLoading: false,
      pickList: [],
      pickSearched: false
    }
  },
  computed: {
    activeAgents() {
      return this.agentOptions.filter(a => a.status === 'active')
    },
    // 右侧列表所需的精简结构（原始字段为 agent_id / display_name）
    chatAgentList() {
      return this.activeAgents.map(a => ({
        id: a.agent_id,
        name: a.display_name || a.name || '数字员工'
      }))
    },
    // 顶栏标题：当前选中的数字员工名称
    currentAgentName() {
      const a = this.agentOptions.find(x => x.agent_id === this.agentId)
      return a ? (a.display_name || a.name || '数字员工') : '请选择数字员工'
    },
    // 当前对话对象作为队长的生效团队（无则单 Agent 对话）
    currentTeam() {
      if (!this.agentId) return null
      return this.teams.find(t => t.leader_agent_id === this.agentId && t.status === 'active') || null
    },
    // 团队成员分工提示（role 即注入规划提示词的分工说明）
    teamTip() {
      if (!this.currentTeam) return ''
      const list = (this.currentTeam.members || []).map(m => {
        const a = this.agentOptions.find(x => x.agent_id === m.agent_id)
        const name = a ? (a.display_name || a.name) : m.agent_id
        return `${name}：${m.role || '未指定分工'}`
      })
      return `团队编排已启用。成员分工：${list.length ? list.join('；') : '暂无成员'}`
    }
  },
  mounted() {
    // 管理员无 owner，常驻挂载下发 owner 型请求会 1002 弹错，跳过自动加载
    if (isAdminAcc()) return
    this.loadAgents()
    this.loadTeams()
  },
  beforeDestroy() {
    this.abort()
  },
  methods: {
    // 供主入口 Tab 激活联动（index.vue watch 调用），每次激活都刷新，
    // 保证右侧员工列表与「数字员工」Tab 的新增/删除同步
    onActivate() {
      this.loadAgents()
      this.loadTeams()
    },
    // 生效团队列表：仅企业账号可能有团队（个人无创建权限）；失败保持空，不影响单 Agent 对话
    loadTeams() {
      if (!isEnterprise()) { this.teams = []; return }
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentTeamsList,
        agentBody({ page: 1, limit: 0, status: 'active' })).then((res) => {
        if (res.data.code == 0) this.teams = res.data.data.data || []
      }).catch(() => {})
    },
    loadAgents() {
      return this.$tools.asyncPost(this, this.$baseUrl.aiAgentAgentsList,
        agentBody({ limit: 0 })).then((res) => {
        if (res.data.code == 0) {
          this.agentOptions = res.data.data.data || []
          // 上抛可用员工列表（已映射为 { id, name }），供右侧功能列下方渲染选择列表
          this.$emit('agents-change', this.chatAgentList)
        }
      }).catch(() => {})
    },
    // 右侧列表点选：与顶栏下拉同源，同一员工不重置当前会话
    selectAgent(id) {
      if (!id || id === this.agentId) return
      this.agentId = id
      this.onAgentChange()
    },
    onAgentChange() {
      this.abort()
      this.currentSessionId = ''
      this.messages = []
      // 先清空左侧会话列表，再按新员工拉取，避免切换瞬间仍显示上一个员工的会话
      this.sessionList = []
      this.loadSessions()
      this.$emit('agent-change', this.agentId)
    },
    loadSessions() {
      if (!this.agentId) { this.sessionList = []; return }
      this.sessionLoading = true
      const data = { page: 1, limit: 50, agent_id: this.agentId }
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentSessionsList, agentBody(data)).then((res) => {
        if (res.data.code == 0) this.sessionList = res.data.data.data || []
        this.sessionLoading = false
      }).catch(() => { this.sessionLoading = false })
    },
    newSession() {
      this.abort()
      this.currentSessionId = ''
      this.messages = []
    },
    switchSession(s) {
      if (s.session_id === this.currentSessionId) return
      this.abort()
      this.currentSessionId = s.session_id
      this.loadHistory()
    },
    loadHistory() {
      // 接口按 ctime 倒序（最新在前），reverse 成正序渲染
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentSessionsMessages,
        agentBody({ session_id: this.currentSessionId, page: 1, limit: 50 })).then((res) => {
        if (res.data.code == 0) {
          const list = (res.data.data.data || []).slice().reverse()
          this.messages = list.map(m => {
            if (m.role === 'user') return { role: 'user', content: m.content, turnId: m.turn_id }
            if (m.role === 'tool') {
              // 工具调用记录：[{name,args,result,blocked}] JSON 数组；截断/异常数据解析失败时
              // items 置 null 走原文折叠降级（置 [] 会让空面板遮住降级分支）
              let records = null
              try {
                const a = JSON.parse(m.content)
                if (Array.isArray(a) && a.length) records = a
              } catch (e) { records = null }
              const items = records ? records.map(r => ({
                kind: (r.blocked || r.failed) ? 'tool_error' : 'tool_result',
                name: r.name || '', args: r.args || '', result: r.result || '', open: false
              })) : null
              return { role: 'tool', content: m.content, turnId: m.turn_id, items: items, open: false }
            }
            return { role: 'assistant', content: m.content, html: renderMarkdown(m.content), tools: [], turnId: m.turn_id }
          })
          this.scrollToBottom(true)
        }
      }).catch(() => {})
    },
    onKeydown(e) {
      if (e.isComposing) return
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send() }
    },
    send(reuseUserMsg) {
      if (this.sending) return this.$message({ type: 'warning', message: '会话正在处理中，请稍后再试' })
      if (!this.agentId) return this.$message({ type: 'warning', message: '请先选择数字员工' })
      const userMsg = reuseUserMsg || { role: 'user', content: this.input.trim(), turnId: genTurnId() }
      if (!userMsg.content) return
      if (reuseUserMsg && userMsg.dead) {
        userMsg.turnId = genTurnId() // 服务端已将旧 id 收据置为 failed，换新 id 才能重新执行
        userMsg.dead = false
      }
      if (!reuseUserMsg) {
        this.messages.push(userMsg)
        this.input = ''
      }
      const asst = { role: 'assistant', content: '', html: '', thinks: '', tools: [], state: 'streaming', thinkOpen: ['t'], turnId: userMsg.turnId }
      this.messages.push(asst)
      this.sending = true
      this.autoScroll = true
      this.scrollToBottom(true)
      this.abortCtrl = new AbortController()
      const req = {
        session_id: this.currentSessionId || '',
        agent_id: this.agentId,
        client_turn_id: userMsg.turnId,
        content: userMsg.content
      }
      const done = (result) => {
        if (!this.currentSessionId && result.session_id) {
          this.currentSessionId = result.session_id
          this.loadSessions()
        }
        asst.content = result.reply || asst.content
        // ok 载荷 tools 仅缓存回放时下发（流式正常路径前端已收到事件卡片）；非流式响应始终携带
        if (result.tools && result.tools.length && !asst.tools.length) {
          asst.tools = result.tools.map(t => ({
            kind: (t.blocked || t.failed) ? 'tool_error' : 'tool_result',
            name: t.name || '', args: t.args || '', result: t.result || '', open: false
          }))
        }
        this.flushMd(asst, true)
        asst.state = 'done'
        asst.thinkOpen = []
        this.sending = false
      }
      const fail = (msg, dead) => {
        this.$message({ type: 'error', message: msg })
        // 终态渲染：失败前已收到的部分回复同步到 html，保证 content 与展示一致
        this.flushMd(asst, true)
        asst.state = 'failed'
        // dead=服务端已将该轮收据置为 failed，重试须换新 id；断线/中断保持原 id 走缓存恢复
        userMsg.dead = !!dead
        this.sending = false
      }
      if (this.streamEnabled) {
        ssePost(this.$baseUrl.aiAgentChatStream, req, {
          onMessage: (evt) => this.applyEvent(asst, evt),
          onOk: done,
          // kind='error' 为服务端终态错误（收据已 failed）→ 换新 id；"正在处理中"除外（收据仍 running，可等缓存）
          onError: (msg, kind) => fail(msg, kind === 'error' && msg.indexOf('正在处理中') < 0),
          // chat 有 client_turn_id 幂等：断连重试可取回缓存回复（其他 SSE 接口无此语义）
          disconnectMsg: '连接中断，可重试（复用原 client_turn_id 可取回缓存回复）'
        }, this.abortCtrl.signal)
      } else {
        this.$tools.asyncPost(this, this.$baseUrl.aiAgentChat, agentBody(req)).then((res) => {
          if (res.data.code == 0) done(res.data.data)
        }).catch((err) => {
          // 业务码拦截器已统一弹错（含 3001）；网络层静默 reject，这里兜底提示
          if (err && err.isAxiosError) fail('请求失败，请稍后重试')
          else if (err && err.message) fail(err.message, err.message.indexOf('正在处理中') < 0)
        })
      }
    },
    applyEvent(asst, evt) {
      if (evt.kind === 'think') asst.thinks += evt.content || ''
      else if (evt.kind === 'reply') {
        asst.content += evt.content || ''
        this.flushMd(asst, false)
      } else if (evt.kind === 'tool_call' || evt.kind === 'tool_result' || evt.kind === 'tool_error') {
        asst.tools.push({
          kind: evt.kind, name: evt.name || '',
          args: evt.kind === 'tool_call' ? (evt.content || '') : '',
          result: evt.kind === 'tool_call' ? '' : (evt.content || ''),
          open: false
        })
      }
      this.scrollToBottom()
    },
    // 流式 markdown 渲染节流：仅在无待处理节流窗口时立即渲染，窗口内增量合并由定时器统一渲染
    // （final=true 终态立即渲染并清 timer）；不节流的话每个 chunk 都会完整重解析 + 重建 innerHTML
    flushMd(asst, final) {
      if (final) {
        if (this._mdTimer) { clearTimeout(this._mdTimer); this._mdTimer = null }
        asst.html = renderMarkdown(asst.content)
        return
      }
      if (this._mdTimer) return
      asst.html = renderMarkdown(asst.content)
      this._mdTimer = setTimeout(() => {
        this._mdTimer = null
        // 定时器期间可能已切会话/重试：消息仍在列表且未完成才继续渲染
        if (this.messages.indexOf(asst) >= 0 && asst.state === 'streaming') {
          asst.html = renderMarkdown(asst.content)
        }
      }, 200)
    },
    retryTurn(asst) {
      const idx = this.messages.indexOf(asst)
      if (idx < 1) return
      this.messages.splice(idx, 1)
      this.send(this.messages[idx - 1])
    },
    // 主动中断当前流：服务端会继续跑完并落库，点重试可取回缓存回复
    stopStream() {
      this.abort()
    },
    abort() {
      if (this._mdTimer) { clearTimeout(this._mdTimer); this._mdTimer = null }
      if (this.abortCtrl) {
        this.abortCtrl.abort()
        this.abortCtrl = null
      }
      this.sending = false
      const last = this.messages[this.messages.length - 1]
      if (last && last.role === 'assistant' && last.state === 'streaming') {
        last.state = 'failed'
        // 节流窗口内未合入 html 的尾部 chunk 补一次终渲，保证展示与 content 一致
        this.flushMd(last, true)
      }
    },
    onScroll() {
      const el = this.$refs.msgBox
      if (!el) return
      this.autoScroll = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    },
    scrollToBottom(force) {
      if (!force && !this.autoScroll) return
      this.$nextTick(() => {
        const el = this.$refs.msgBox
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    /* ---------- 推荐通用技能 ---------- */
    openSkillPick() {
      this.pickVisible = true
      this.pickQuery = this.input.trim()
      // 快照原输入：搜索词仅用于匹配，弹窗内改动不应导致原文丢失
      this.pickOrigInput = this.input
      this.pickList = []
      this.pickSearched = false
    },
    searchSkills() {
      const q = this.pickQuery.trim()
      if (!q) return this.$message({ type: 'warning', message: '请输入任务场景描述' })
      this.pickLoading = true
      this.$tools.asyncPost(this, this.$baseUrl.aiAgentGenSkillsSelect,
        agentBody({ query: q, top_n: 5 })).then((res) => {
        if (res.data.code == 0) this.pickList = res.data.data && res.data.data.data ? res.data.data.data : []
        this.pickSearched = true
        this.pickLoading = false
      }).catch((err) => {
        this.pickLoading = false
        if (err && err.isAxiosError) this.$message({ type: 'error', message: '网络异常，请稍后重试' })
      })
    },
    // 插入引用文案到输入框，由用户确认后手动发送；任务描述优先用打开时的原输入
    insertSkill(s) {
      const task = this.pickOrigInput.trim() || this.pickQuery.trim()
      this.input = '请使用通用技能【' + s.name + '】处理以下任务：' + task
      this.pickVisible = false
    },
    fmtTime(s) {
      if (!s) return ''
      return this.$tools.parserDateTime('datetime', s) || s.substring(0, 19).replace('T', ' ')
    }
  }
}
</script>

<style lang="less" scoped>
.chat_wrap {
  display: flex;
  gap: 16px;
  height: calc(100vh - 160px);
  min-height: 480px;
  overflow: hidden;  // 防止子元素溢出
  border-radius: 8px;
  // 右侧整列：对话区（数字员工列表在页面右侧功能列下方）
  .chat_right {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
}
.chat_side {
  width: 220px;
  flex-shrink: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  .side_head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #ebeef5;
    .side_label {
      font-weight: bold;
    }
  }
  .side_list {
    flex: 1;
    overflow-y: auto;
    .side_item {
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: 1px solid #f2f6fc;
      &.active {
        background: #ecf5ff;
        .side_title { color: #409eff; }
      }
      &:hover {
        background: #f5f7fa;
      }
      .side_title {
        font-size: 13px;
        color: #303133;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .side_time {
        margin-top: 4px;
        font-size: 12px;
        color: #909399;
      }
    }
    .side_empty {
      padding: 24px 0;
      text-align: center;
      color: #909399;
      font-size: 13px;
    }
  }
}
.chat_main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;  // 防止内容溢出容器
  .chat_head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #ebeef5;
    // 顶栏当前对话员工
    .chat_agent {
      display: flex;
      align-items: center;
      min-width: 0;
      .chat_agent_icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 50%;
        background: #ecf5ff;
        color: #409eff;
        font-size: 14px;
      }
      .chat_agent_name {
        font-size: 15px;
        font-weight: bold;
        color: #303133;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .team_badge { margin-left: 10px; flex: 0 0 auto; }
    }
    .stream_switch {
      font-size: 13px;
      color: #606266;
    }
  }
  .msg_box {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    .msg_row {
      margin-bottom: 16px;
      width: 100%;  // 确保占满整行
      // 裸 role 类名会被全局 .user{position:fixed} 污染，统一加前缀
      &.msg_user {
        text-align: right;
      }
    }
    .bubble {
      display: inline-block;
      max-width: 80%;
      min-width: 80px;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-word;
      text-align: left;
    }
    .user_bubble {
      background: #409eff;
      color: #fff;
    }
    // 历史 role=tool 消息块（与 asst_block 平级）
    .tool_hist {
      margin-bottom: 4px;
      text-align: left;
      .tool_hist_head {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border: 1px dashed #dcdfe6;
        border-radius: 4px;
        background: #fafafa;
        font-size: 12px;
        color: #909399;
        cursor: pointer;
        user-select: none;
        &:hover { background: #f5f7fa; }
      }
      .tool_hist_body {
        margin: 6px 0;
        max-width: 80%;
      }
      .tool_hist_raw {
        margin: 6px 0;
        padding: 8px 10px;
        border: 1px dashed #dcdfe6;
        border-radius: 4px;
        font-size: 12px;
        color: #909399;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 240px;
        overflow-y: auto;
      }
    }
    .asst_block {
      display: block;  // 改为 block，独占一行
      max-width: 80%;
      min-width: 80px;
      text-align: left;
      .thinking_bar {
        display: inline-block;
        padding: 8px 12px;
        border-radius: 4px;
        background: #f4f4f5;
        color: #909399;
        font-size: 13px;
      }
      .think_collapse {
        margin-bottom: 8px;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        /deep/ .el-collapse-item__header {
          height: 34px;
          line-height: 34px;
          padding: 0 10px;
          background: #fafafa;
          font-size: 13px;
          color: #909399;
        }
        /deep/ .el-collapse-item__wrap {
          border-bottom: none;
        }
        .think_text {
          margin: 0;
          padding: 8px 10px;
          font-size: 12px;
          color: #909399;
          white-space: pre-wrap;
          word-break: break-word;
          max-height: 240px;
          overflow-y: auto;
        }
      }
      .asst_bubble {
        display: block;  // 撑满 asst_block 宽度
        background: #f4f4f5;
        color: #303133;
      }
      // markdown 渲染区：覆盖 .bubble 的 pre-wrap；v-html 内容无 scoped attr，需 /deep/ 穿透
      .md_body {
        white-space: normal;
        /deep/ p { margin: 0 0 8px; &:last-child { margin-bottom: 0; } }
        /deep/ h1, h2, h3, h4, h5 {
          margin: 12px 0 6px;
          font-size: 15px;
          color: #303133;
          &:first-child { margin-top: 0; }
        }
        /deep/ ul, ol { margin: 0 0 8px; padding-left: 20px; }
        /deep/ li { margin: 2px 0; }
        /deep/ a { color: #409eff; }
        /deep/ code {
          padding: 1px 4px;
          border-radius: 3px;
          background: #eef1f6;
          color: #c7254e;
          font-size: 13px;
        }
        /deep/ pre {
          margin: 0 0 8px;
          padding: 8px 10px;
          border-radius: 4px;
          background: #282c34;
          overflow-x: auto;
          code { padding: 0; background: transparent; color: #abb2bf; }
        }
        /deep/ blockquote {
          margin: 0 0 8px;
          padding: 4px 10px;
          border-left: 3px solid #dcdfe6;
          color: #909399;
          p { margin: 0; }
        }
        /deep/ table {
          margin: 0 0 8px;
          border-collapse: collapse;
          font-size: 13px;
          th, td { border: 1px solid #dcdfe6; padding: 4px 8px; }
          th { background: #f5f7fa; }
        }
        /deep/ img { max-width: 100%; }
      }
      .fail_bar {
        margin-top: 4px;
        font-size: 12px;
        color: #f56c6c;
      }
    }
    .msg_empty {
      padding: 60px 0;
      text-align: center;
      color: #909399;
      font-size: 13px;
    }
  }
  .input_tools {
    padding: 6px 12px 0;
    border-top: 1px solid #ebeef5;
  }
  .input_bar {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 6px 12px 10px;
    .send_btn {
      /deep/ &.el-button {
        height: 40px;  // 与 textarea 底部对齐
        padding: 0 16px;
      }
    }
  }
}
.pick_bar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.pick_list {
  max-height: 360px;
  overflow-y: auto;
  .pick_item {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px 10px;
    margin-bottom: 8px;
    .pick_head {
      display: flex;
      align-items: center;
      gap: 8px;
      b { font-size: 13px; }
    }
    .pick_summary {
      font-size: 12px;
      color: #606266;
      padding: 4px 0;
    }
    .pick_meta {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 12px;
      color: #909399;
    }
  }
}
.pick_empty {
  padding: 24px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
</style>
